import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getPaymentMethods, addPaymentMethod, updateDefaultPaymentMethod, removePaymentMethod, createSetupIntent, getCurrentMembership, updateAutoRenewal, getCreditBalance } from '../services/api';
import Alert from '../components/common/Alert';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function AddPaymentMethodForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create setup intent
      const { client_secret } = await createSetupIntent();

      // Confirm card setup
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Save payment method to backend
      await addPaymentMethod({ payment_method_id: setupIntent.payment_method });

      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to add payment method');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="p-3 border border-gray-300 rounded-md">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <Alert type="error" message={error} onClose={() => setError('')} />
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading ? 'Adding...' : 'Add Card'}
        </button>
      </div>
    </form>
  );
}

function BillingSettings() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchPaymentMethods(), fetchMembership(), fetchCreditBalance()]);
  };

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const data = await getPaymentMethods();
      setPaymentMethods(data.payment_methods || []);
    } catch (err) {
      setError('Failed to load payment methods');
      console.error('Error fetching payment methods:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembership = async () => {
    try {
      const data = await getCurrentMembership();
      setCurrentMembership(data);
    } catch (err) {
      console.log('No active membership found');
    }
  };

  const fetchCreditBalance = async () => {
    try {
      const data = await getCreditBalance();
      setCreditBalance(data.balance || 0);
    } catch (err) {
      console.log('Failed to load credit balance');
      setCreditBalance(0);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      setActionLoading(paymentMethodId);
      await updateDefaultPaymentMethod({ payment_method_id: paymentMethodId });
      await fetchPaymentMethods();
    } catch (err) {
      alert('Failed to set default payment method: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (paymentMethodId) => {
    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setActionLoading(paymentMethodId);
      await removePaymentMethod(paymentMethodId);
      await fetchPaymentMethods();
    } catch (err) {
      alert('Failed to remove payment method: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchPaymentMethods();
  };

  const handleToggleAutoRenewal = async () => {
    if (!currentMembership) return;

    const newAutoRenewStatus = !currentMembership.auto_renew;

    // Check if they have a payment method when enabling
    if (newAutoRenewStatus && paymentMethods.length === 0) {
      alert('Please add a payment method before enabling auto-renewal.');
      return;
    }

    if (!newAutoRenewStatus) {
      if (!confirm('Are you sure you want to disable auto-renewal? Your subscription will not renew automatically at the end of the current period.')) {
        return;
      }
    }

    try {
      setAutoRenewLoading(true);
      await updateAutoRenewal({
        membership_id: currentMembership.id,
        auto_renew: newAutoRenewStatus
      });
      await fetchMembership();
      alert(newAutoRenewStatus ? 'Auto-renewal enabled successfully!' : 'Auto-renewal disabled successfully!');
    } catch (err) {
      alert('Failed to update auto-renewal: ' + err.message);
    } finally {
      setAutoRenewLoading(false);
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'text-blue-600',
      mastercard: 'text-red-600',
      amex: 'text-blue-800',
      discover: 'text-orange-600',
    };

    return (
      <div className={`text-2xl font-bold ${brandColors[brand] || 'text-gray-600'}`}>
        {brand?.toUpperCase() || 'CARD'}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Billing Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your payment methods and billing information
          </p>
        </header>

        {/* Credit Balance Card */}
        {creditBalance > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 shadow rounded-lg p-6 border-l-4 border-green-500 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold text-gray-800">Available Credits</h2>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="mt-3">
              <p className="text-4xl font-bold text-green-600">
                ${Number(creditBalance).toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Your credits will be automatically applied to your next payment
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {/* Auto-Renewal Settings */}
        {currentMembership && parseFloat(currentMembership.membership_type?.price) > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Auto-Renewal Settings</h2>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div>
                      <h3 className="text-base font-medium text-gray-900">
                        {currentMembership.membership_type?.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {currentMembership.auto_renew ? (
                          <>
                            <span className="text-green-600 font-medium">Active</span>
                            {currentMembership.current_period_end && (
                              <> - Next billing: {new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString()}</>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-gray-500 font-medium">Disabled</span>
                            {currentMembership.current_period_end && (
                              <> - Expires: {new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString()}</>
                            )}
                          </>
                        )}
                      </p>
                      {currentMembership.auto_renew && paymentMethods.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Payment method: {paymentMethods.find(pm => pm.is_default)?.card?.brand?.toUpperCase() || 'N/A'}
                          {' '}••••{paymentMethods.find(pm => pm.is_default)?.card?.last4 || 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleToggleAutoRenewal}
                    disabled={autoRenewLoading}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      currentMembership.auto_renew ? 'bg-green-600' : 'bg-gray-200'
                    } ${autoRenewLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        currentMembership.auto_renew ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-gray-900">
                    {currentMembership.auto_renew ? 'ON' : 'OFF'}
                  </span>
                </div>
              </div>

              {!currentMembership.auto_renew && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Enable auto-renewal to automatically renew your subscription at the end of the billing period.
                    {paymentMethods.length === 0 && (
                      <span className="block mt-1 text-yellow-600">
                        You need to add a payment method first.
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Payment Methods</h2>
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Payment Method
              </button>
            )}
          </div>

          {/* Add Payment Method Form */}
          {showAddForm && (
            <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded-lg">
              <h3 className="text-md font-medium text-gray-900 mb-4">Add New Payment Method</h3>
              <Elements stripe={stripePromise}>
                <AddPaymentMethodForm
                  onSuccess={handleAddSuccess}
                  onCancel={() => setShowAddForm(false)}
                />
              </Elements>
            </div>
          )}

          {/* Payment Methods List */}
          {paymentMethods.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods</h3>
              <p className="mt-1 text-sm text-gray-500">
                Add a payment method to manage your subscriptions
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`border rounded-lg p-4 ${
                    method.is_default ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getCardBrandIcon(method.card?.brand)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            •••• •••• •••• {method.card?.last4}
                          </p>
                          {method.is_default && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          Expires {method.card?.exp_month}/{method.card?.exp_year}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {!method.is_default && (
                        <button
                          onClick={() => handleSetDefault(method.id)}
                          disabled={actionLoading === method.id}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {actionLoading === method.id ? 'Setting...' : 'Set as Default'}
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(method.id)}
                        disabled={actionLoading === method.id || (method.is_default && paymentMethods.length === 1)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        title={method.is_default && paymentMethods.length === 1 ? 'Cannot remove the only default payment method' : ''}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {actionLoading === method.id ? 'Removing...' : ''}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About Payment Methods</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Your default payment method will be used for automatic renewals</li>
                  <li>All payment information is securely stored with Stripe</li>
                  <li>You can update or remove payment methods at any time</li>
                  <li>Removing a payment method will not cancel your active subscription</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillingSettings;